/**
 * Arabic letter mapping for Saudi plate numbers
 * Based on official Saudi license plate letter mapping
 */
const arabicLetters: Record<string, string> = {
  'A': 'ا',
  'B': 'ب',
  'J': 'ح',
  'D': 'د',
  'R': 'ر',
  'S': 'س',
  'X': 'ص',
  'T': 'ط',
  'E': 'ع',
  'G': 'ق',
  'K': 'ك',
  'L': 'ل',
  'Z': 'م',
  'N': 'ن',
  // Use base letter "ه" (avoid tatweel/presentation variants like "هـ")
  'H': 'ه',
  'U': 'و',
  'V': 'ى',
};

/**
 * Reverse mapping: Arabic to English
 */
const englishLetters: Record<string, string> = {};
Object.entries(arabicLetters).forEach(([eng, arb]) => {
  englishLetters[arb] = eng;
});
// Also map ي (Yeh) to V, but normalize to ى first
englishLetters['\u064A'] = 'V'; // ي -> V (will be normalized to ى)
// Map base ه as H too (common user input)
englishLetters['\u0647'] = 'H'; // ه -> H

/**
 * Check if a string contains Arabic characters
 */
const containsArabic = (text: string): boolean => {
  return /[\u0600-\u06FF]/.test(text);
};

/**
 * Normalize Arabic plate letters: replace ي (Yeh) with ى (Alef Maksura)
 * Saudi license plates use ى not ي
 */
const normalizePlateArabic = (text: string): string => {
  if (!text) return text;
  // Remove tatweel (ـ) and normalize ي to ى
  return text.replace(/\u0640/g, '').replace(/\u064A/g, '\u0649');
};

/**
 * Format plate number: Arabic letters with spaces, then numbers without spaces
 * Example: "ABC123" -> "أ ب ج 123"
 * Example: "ششش2344" -> "ش ش ش 2344"
 */
export const formatPlateNumber = (plate: string): string => {
  if (!plate) return '';
  
  // Normalize Arabic letters first (ي -> ى)
  const normalized = normalizePlateArabic(plate);
  
  // Remove all spaces for processing
  const cleaned = normalized.replace(/\s+/g, '');
  
  // Keep letters in input order so we can handle mixed strings safely
  const lettersInOrder: string[] = [];
  const numbers: string[] = [];
  let sawEnglishLetter = false;
  
  for (const char of cleaned) {
    if (/[\u0600-\u06FF]/.test(char)) {
      // Arabic letter
      // normalizePlateArabic() already removed tatweel and normalized ي -> ى,
      // but we still guard here in case of single-char processing changes later.
      const normalizedChar = normalizePlateArabic(char);
      if (normalizedChar) {
        lettersInOrder.push(normalizedChar);
      }
    } else if (/\d/.test(char)) {
      // Number
      numbers.push(char);
    } else if (/[A-Za-z]/.test(char)) {
      // English letter
      sawEnglishLetter = true;
      const upper = char.toUpperCase();
      lettersInOrder.push(arabicLetters[upper] || upper);
    }
  }
  
  let formattedPlate = '';
  
  if (lettersInOrder.length > 0) {
    // If the string contained any English letters, treat it as "storage order" (LTR)
    // and reverse for correct Arabic display order.
    // This also fixes legacy mixed values like "DUه1401" which would otherwise render weirdly.
    formattedPlate = (sawEnglishLetter ? [...lettersInOrder].reverse() : lettersInOrder).join(' ');
  }
  
  // Numbers without spaces
  const numbersPart = numbers.join('');
  
  // Combine with space between letters and numbers
  if (formattedPlate && numbersPart) {
    return `${formattedPlate} ${numbersPart}`;
  } else if (formattedPlate) {
    return formattedPlate;
  } else if (numbersPart) {
    return numbersPart;
  }
  
  return plate;
};

/**
 * Parse formatted plate number back to simple format (for storage)
 * Example: "أ ب ج 123" -> "ABC123"
 * If the input already contains Arabic (manual input), return it as-is
 */
export const parsePlateNumber = (formattedPlate: string): string => {
  if (!formattedPlate) return '';
  
  // Normalize Arabic letters first (ي -> ى)
  const normalized = normalizePlateArabic(formattedPlate);
  
  // If the plate already contains Arabic characters (manual input), normalize and process
  if (containsArabic(normalized)) {
    // Remove spaces and extract letters and numbers
    const cleaned = normalized.replace(/\s+/g, '');
    const letters: string[] = [];
    const numbers: string[] = [];
    
    for (const char of cleaned) {
      if (/[\u0600-\u06FF]/.test(char)) {
        // Arabic letter - convert to English
        letters.push(englishLetters[char] || char);
      } else if (/\d/.test(char)) {
        // Number
        numbers.push(char);
      }
    }
    
    // Reverse letters (because Arabic is RTL but we store LTR)
    // "س ل ط" in Arabic display = "TLS" in English storage
    // When converting back, we need to reverse to match storage format
    return `${letters.reverse().join('')}${numbers.join('')}`.toUpperCase();
  }
  
  // If already in English format, just clean it up
  return normalized.replace(/\s+/g, '').toUpperCase();
};

